/**
 * Print Center P4 — coworker SPA client for /api/my/print-jobs/* and
 * /api/printers/:id/public.
 *
 * Mirrors `apps/admin/src/api/printers.ts` style: every function takes the
 * Auth0 access `token` explicitly. The multipart upload helper deliberately
 * bypasses the JSON-only `apiRequest` (which `JSON.stringify`s the body) —
 * it constructs a `FormData` directly so binary bytes survive.
 */
import type { PrintJob } from '@hub-wifi/shared'
import { ApiError } from './client'

const API_URL = import.meta.env.VITE_API_URL as string

async function readErrorBody(resp: Response): Promise<unknown> {
  try {
    return await resp.json()
  } catch {
    return {}
  }
}

export async function uploadPrintJob({
  token,
  file,
}: {
  token: string
  file: File
}): Promise<PrintJob> {
  const form = new FormData()
  form.append('file', file, file.name)
  const resp = await fetch(`${API_URL}/api/my/print-jobs`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  })
  const body = await resp.json().catch(() => ({}))
  if (!resp.ok) {
    throw new ApiError((body as { error?: string }).error ?? 'Upload failed', resp.status, body)
  }
  return body as PrintJob
}

export async function listMyPrintJobs(token: string): Promise<PrintJob[]> {
  const resp = await fetch(`${API_URL}/api/my/print-jobs`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const body = await resp.json().catch(() => ({}))
  if (!resp.ok) {
    throw new ApiError((body as { error?: string }).error ?? 'List failed', resp.status, body)
  }
  return (body as { items: PrintJob[] }).items ?? []
}

export async function releasePrintJob({
  token,
  jobId,
  printerId,
  via,
}: {
  token: string
  jobId: string
  printerId: string
  via: 'qr_scan' | 'manual_pick'
}): Promise<PrintJob> {
  const resp = await fetch(`${API_URL}/api/my/print-jobs/${jobId}/release`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ printerId, via }),
  })
  const body = await resp.json().catch(() => ({}))
  if (!resp.ok) {
    throw new ApiError((body as { error?: string }).error ?? 'Release failed', resp.status, body)
  }
  return body as PrintJob
}

export async function cancelPrintJob({
  token,
  jobId,
}: {
  token: string
  jobId: string
}): Promise<void> {
  const resp = await fetch(`${API_URL}/api/my/print-jobs/${jobId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!resp.ok) {
    const body = await readErrorBody(resp)
    throw new ApiError((body as { error?: string }).error ?? 'Cancel failed', resp.status, body)
  }
}

export interface PublicPrinter {
  id: string
  name: string
  buildingId: string
  buildingName: string | null
  status: 'active'
  online: boolean
  epsonConnected: boolean | null
  connectionCheckedAt: string | null
  lastSeenConnectedAt: string | null
}

export async function getPublicPrinter({
  token,
  printerId,
}: {
  token: string
  printerId: string
}): Promise<PublicPrinter> {
  const resp = await fetch(`${API_URL}/api/printers/${printerId}/public`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const body = await resp.json().catch(() => ({}))
  if (!resp.ok) {
    throw new ApiError((body as { error?: string }).error ?? 'Printer not found', resp.status, body)
  }
  return body as PublicPrinter
}

export interface BuildingPrinter {
  id: string
  name: string
  buildingId: string
  status: string
  productName: string | null
  lastSeenConnectedAt: string | null
  epsonConnected: boolean | null
  connectionCheckedAt: string | null
}

export async function listBuildingPrinters({
  token,
  buildingId,
}: {
  token: string
  buildingId: string
}): Promise<BuildingPrinter[]> {
  const resp = await fetch(`${API_URL}/api/my/buildings/${buildingId}/printers`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const body = await resp.json().catch(() => ({}))
  if (!resp.ok) {
    throw new ApiError((body as { error?: string }).error ?? 'List failed', resp.status, body)
  }
  return (body as { printers: BuildingPrinter[] }).printers ?? []
}
